import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shield, ArrowLeft, Search, UserPlus } from "lucide-react";
import { api, apiErrorMessage } from "../../api";
import { useAuth } from "../../context/AuthContext";

const ROLES = [
  { value: "none", label: "None (revoke)" },
  { value: "readonly", label: "Read-only" },
  { value: "support", label: "Support" },
  { value: "admin", label: "Admin" },
];

function RoleBadge({ role }) {
  const styles = {
    admin: "bg-coral text-white",
    support: "bg-forest text-white",
    readonly: "bg-white text-forest border border-forest/30",
    none: "bg-cream text-forest/60",
  };
  return (
    <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${styles[role] || styles.none}`}>
      {role}
    </span>
  );
}

export default function BenchTeam() {
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const list = await api.platformStaff();
      setStaff(list);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (!q.trim()) { setResults([]); return; }
      try {
        const r = await api.platformSearchUsers(q.trim());
        setResults(r);
      } catch (e) {
        setResults([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const setRole = async (uid, role) => {
    setMsg("");
    try {
      await api.platformSetStaffRole(uid, role);
      setMsg("Role updated");
      await load();
    } catch (e) {
      setMsg(apiErrorMessage(e, "Failed to update role"));
    }
  };

  return (
    <div className="min-h-screen bg-cream-2">
      <div className="bg-forest text-white">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="flex items-center gap-3">
            <Shield size={22} className="text-coral" />
            <div>
              <div className="text-xs uppercase tracking-wide text-white/50">The Bench — Platform</div>
              <h1 className="font-display text-3xl">Bench Team</h1>
            </div>
            <Link to="/staff" className="ml-auto text-sm text-white/70 hover:text-white flex items-center gap-1">
              <ArrowLeft size={14} /> Staff Console
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {msg && (
          <div className="rounded bg-forest/5 text-forest px-3 py-2 text-sm" data-testid="bench-msg">
            {msg}
          </div>
        )}

        <section className="bg-white rounded-lg border border-cream">
          <div className="px-4 py-3 border-b border-cream flex items-center gap-2">
            <UserPlus size={16} className="text-coral" />
            <div className="font-medium text-forest">Promote user to Bench staff</div>
          </div>
          <div className="p-4 space-y-3">
            <div className="relative max-w-md">
              <Search size={14} className="absolute left-3 top-2.5 text-forest/40" />
              <input
                data-testid="bench-user-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search users by email or name…"
                className="w-full pl-8 pr-3 py-1.5 rounded border border-cream text-sm"
              />
            </div>
            {results.length > 0 && (
              <div className="divide-y divide-cream border border-cream rounded">
                {results.map((u) => (
                  <div key={u.id} className="px-3 py-2 flex items-center gap-3" data-testid={`user-row-${u.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-forest truncate">{u.name}</div>
                      <div className="text-xs text-forest/60 truncate">{u.email}</div>
                    </div>
                    <RoleBadge role={u.platform_role} />
                    <select
                      data-testid={`user-role-${u.id}`}
                      defaultValue={u.platform_role}
                      onChange={(e) => setRole(u.id, e.target.value)}
                      className="border border-cream rounded text-xs px-2 py-1"
                      disabled={u.id === user?.id}
                    >
                      {ROLES.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="bg-white rounded-lg border border-cream">
          <div className="px-4 py-3 border-b border-cream font-medium text-forest">
            Current Bench staff
          </div>
          {loading ? (
            <div className="p-6 text-forest/60 text-sm">Loading…</div>
          ) : (
            <div className="divide-y divide-cream" data-testid="bench-staff-list">
              {staff.map((s) => (
                <div key={s.id} className="px-4 py-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-forest truncate">{s.name}</div>
                    <div className="text-xs text-forest/60 truncate">{s.email}</div>
                  </div>
                  <RoleBadge role={s.platform_role} />
                  <select
                    data-testid={`staff-role-${s.id}`}
                    value={s.platform_role}
                    onChange={(e) => setRole(s.id, e.target.value)}
                    className="border border-cream rounded text-xs px-2 py-1"
                    disabled={s.id === user?.id}
                  >
                    {ROLES.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
              ))}
              {staff.length === 0 && (
                <div className="px-4 py-6 text-forest/50 text-sm">No staff yet. Search a user above to promote.</div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
